import { type KeyboardEvent } from 'react';

import { type IResultAnalyticsData } from '../../../../interface/state/IElectionState';
import { CheckSVG, CopySVG, SearchSVG } from '../../../utils/svgs';
import { UnreadPill } from '../../../utils/unread-pill';
import {
	childEntityLabel,
	COPY_ALL_SENTINEL,
	getUploadedLabel,
	type IDerivedStats,
	puStatus,
	type RowCoverage,
	type SortMode,
	type StatusFilter,
} from './utils';

// Themed warning glyph (currentColor) — adapts to light/dark unlike the shared AlertSVG.
const WarnIcon = () => (
	<svg
		viewBox='0 0 20 20'
		fill='none'
		aria-hidden='true'
	>
		<path
			d='M10 6.7v3.6M10 13.4h.01M8.6 2.6 1.7 14.5c-.6 1 .1 2.3 1.4 2.3h13.8c1.3 0 2-1.3 1.4-2.3L11.4 2.6c-.6-1.1-2.2-1.1-2.8 0Z'
			stroke='currentColor'
			strokeWidth='1.6'
			strokeLinecap='round'
			strokeLinejoin='round'
		/>
	</svg>
);

// ────────────────────────────────────────────────────────────
// Progress ring (token-themed, dark-mode safe)
// ────────────────────────────────────────────────────────────
const ProgressRing = ({ percentage }: { percentage: number }) => {
	const radius = 34;
	const circumference = 2 * Math.PI * radius;
	const offset = circumference - (Math.min(Math.max(percentage, 0), 100) / 100) * circumference;

	return (
		<div className='qa-ring'>
			<svg
				viewBox='0 0 80 80'
				className='qa-ring__svg'
			>
				<circle
					className='qa-ring__track'
					cx='40'
					cy='40'
					r={radius}
					fill='none'
					strokeWidth='7'
				/>
				<circle
					className='qa-ring__value'
					cx='40'
					cy='40'
					r={radius}
					fill='none'
					strokeWidth='7'
					strokeLinecap='round'
					strokeDasharray={circumference}
					strokeDashoffset={offset}
					transform='rotate(-90 40 40)'
				/>
			</svg>
			<span className='qa-ring__label'>{Math.round(percentage)}%</span>
		</div>
	);
};

interface IAnalyticsHeaderProps {
	stats: IDerivedStats;
	entityLabel: string;
	onCopyAll: () => void;
	copiedAll: boolean;
	hasCodes: boolean;
}

export const AnalyticsHeader = ({ stats, entityLabel, onCopyAll, copiedAll, hasCodes }: IAnalyticsHeaderProps) => (
	<div className='qa-header'>
		<div className='qa-header__top'>
			<div className='qa-header__heading'>
				<span className='qa-header__eyebrow'>Result Analytics</span>
				<h3 className='qa-header__title'>{entityLabel} progress</h3>
			</div>
			<ProgressRing percentage={stats.percentage} />
		</div>

		<div className='qa-header__stats'>
			<div className='qa-stat'>
				<span className='qa-stat__value'>{stats.total.toLocaleString()}</span>
				<span className='qa-stat__label'>Expected</span>
			</div>
			<div className='qa-stat qa-stat--success'>
				<span className='qa-stat__value'>{stats.uploaded.toLocaleString()}</span>
				<span className='qa-stat__label'>Uploaded</span>
			</div>
			<div className='qa-stat qa-stat--pending'>
				<span className='qa-stat__value'>{stats.remaining.toLocaleString()}</span>
				<span className='qa-stat__label'>Remaining</span>
			</div>
		</div>

		<div className='qa-header__bar'>
			<span
				className='qa-header__bar-fill'
				style={{ width: `${stats.percentage}%` }}
			/>
		</div>

		{hasCodes && (
			<button
				type='button'
				className='qa-copy-all'
				onClick={onCopyAll}
				aria-label={`Copy all ${entityLabel.toLowerCase()} codes`}
			>
				<span className='qa-copy-all__icon'>
					{copiedAll ? (
						<CheckSVG
							width='15'
							height='15'
							color='currentColor'
						/>
					) : (
						<CopySVG />
					)}
				</span>
				{copiedAll ? 'Copied all codes' : 'Copy all codes'}
			</button>
		)}
	</div>
);

// ────────────────────────────────────────────────────────────
// Toolbar
// ────────────────────────────────────────────────────────────
const STATUS_TABS: { value: StatusFilter; label: string }[] = [
	{ value: 'all', label: 'All' },
	{ value: 'uploaded', label: 'Uploaded' },
	{ value: 'pending', label: 'Pending' },
	{ value: 'conflict', label: 'Conflict' },
];

interface IPuToolbarProps {
	search: string;
	onSearch: (value: string) => void;
	statusFilter: StatusFilter;
	onStatusFilter: (value: StatusFilter) => void;
	sort: SortMode;
	onSort: (value: SortMode) => void;
}

export const PuToolbar = ({ search, onSearch, statusFilter, onStatusFilter, sort, onSort }: IPuToolbarProps) => (
	<div className='qa-toolbar'>
		<label className='qa-search'>
			<span className='qa-search__icon'>
				<SearchSVG fill='currentColor' />
			</span>
			<input
				type='search'
				className='qa-search__input'
				placeholder='Search by code…'
				value={search}
				onChange={(e) => onSearch(e.target.value)}
				aria-label='Search polling units by code'
			/>
		</label>

		<div
			className='qa-segmented'
			role='tablist'
			aria-label='Filter by status'
		>
			{STATUS_TABS.map((tab) => (
				<button
					key={tab.value}
					type='button'
					role='tab'
					aria-selected={statusFilter === tab.value}
					className={`qa-segmented__item ${statusFilter === tab.value ? 'qa-segmented__item--active' : ''}`}
					onClick={() => onStatusFilter(tab.value)}
				>
					{tab.label}
				</button>
			))}
		</div>

		<button
			type='button'
			className='qa-sort'
			onClick={() => onSort(sort === 'uploadedFirst' ? 'code' : 'uploadedFirst')}
			aria-label='Toggle sort order'
		>
			Sort: {sort === 'uploadedFirst' ? 'Status' : 'Code'}
		</button>
	</div>
);

// ────────────────────────────────────────────────────────────
// Row
// ────────────────────────────────────────────────────────────
export const StatusBadge = ({ status }: { status: ReturnType<typeof puStatus> }) => {
	if (status === 'uploaded') return <span className='qa-badge qa-badge--uploaded'>Uploaded</span>;
	if (status === 'conflict')
		return (
			<span className='qa-badge qa-badge--conflict'>
				<span className='qa-badge__icon'>
					<WarnIcon />
				</span>
				Conflict
			</span>
		);
	return <span className='qa-badge qa-badge--pending'>Pending</span>;
};

// Small inline chip showing who covers this child entity (or that it's a gap).
export const CoverageChip = ({ coverage, loading }: { coverage?: RowCoverage; loading?: boolean }) => {
	if (loading)
		return (
			<span
				className='qa-coverage qa-coverage--loading'
				aria-hidden='true'
			/>
		);
	if (!coverage) return <span className='qa-coverage qa-coverage--muted'>Tap to view agent</span>;
	if (coverage.state === 'unassigned') return <span className='qa-coverage qa-coverage--gap'>Unassigned</span>;
	return (
		<span className={`qa-coverage qa-coverage--${coverage.state}`}>
			<span
				className={`qa-coverage__dot ${coverage.isActive ? 'is-active' : 'is-inactive'}`}
				aria-hidden='true'
			/>
			{coverage.agentName || 'Assigned agent'}
		</span>
	);
};

interface IPuRowProps {
	item: IResultAnalyticsData;
	isCopied: boolean;
	onCopy: (value: string, key: string) => void;
	onSelectChild: (item: IResultAnalyticsData) => void;
	coverage?: RowCoverage;
	coverageLoading?: boolean;
	/** Unread messages from this unit's agent, when the parent has a conversation with them. */
	unread?: number;
}

export const PuRow = ({ item, isCopied, onCopy, onSelectChild, coverage, coverageLoading, unread = 0 }: IPuRowProps) => {
	const status = puStatus(item);
	const uploadedLabel = getUploadedLabel(item);

	const select = () => onSelectChild(item);

	return (
		<li className={`qa-row qa-row--${status}`}>
			<div
				className='qa-row__main qa-row__main--actionable'
				role='button'
				tabIndex={0}
				aria-label={`View agent for ${item.code}`}
				onClick={select}
				onKeyDown={(e: KeyboardEvent) => {
					if (e.key === 'Enter' || e.key === ' ') {
						e.preventDefault();
						select();
					}
				}}
			>
				<div className='qa-row__info'>
					<span className='qa-row__code'>{item.code}</span>
					<span className='qa-row__who'>
						<CoverageChip
							coverage={coverage}
							loading={coverageLoading}
						/>
						<UnreadPill
							count={unread}
							className='qa-row__unread'
						/>
					</span>
					{uploadedLabel ? (
						<span className='qa-row__meta'>Uploaded {uploadedLabel}</span>
					) : (
						<span className='qa-row__meta qa-row__meta--muted'>Awaiting upload</span>
					)}
				</div>
				<StatusBadge status={status} />
				<span
					className='qa-row__chevron'
					aria-hidden='true'
				>
					<svg
						width='16'
						height='16'
						viewBox='0 0 16 16'
						fill='none'
					>
						<path
							d='M6 3L11 8L6 13'
							stroke='currentColor'
							strokeWidth='1.5'
							strokeLinecap='round'
							strokeLinejoin='round'
						/>
					</svg>
				</span>
			</div>

			<button
				type='button'
				className={`qa-row__copy ${isCopied ? 'qa-row__copy--done' : ''}`}
				onClick={() => onCopy(item.code, item.code)}
				aria-label={`Copy code ${item.code}`}
			>
				{isCopied ? (
					<CheckSVG
						width='16'
						height='16'
						color='currentColor'
					/>
				) : (
					<CopySVG />
				)}
			</button>
		</li>
	);
};

// ────────────────────────────────────────────────────────────
// List + pager
// ────────────────────────────────────────────────────────────
interface IPuListProps {
	rows: IResultAnalyticsData[];
	copiedCode: string;
	copySuccess: boolean;
	onCopy: (value: string, key: string) => void;
	onSelectChild: (item: IResultAnalyticsData) => void;
	coverage: Map<string, RowCoverage>;
	coverageLoading: boolean;
	currentPage: number;
	totalPages: number;
	onPage: (page: number) => void;
	hasActiveFilter: boolean;
	/** Unread by assigned-agent core user id; the overview passes the peer inbox's map. */
	unreadByAgentId?: ReadonlyMap<number, number>;
}

export const PuList = ({
	rows,
	copiedCode,
	copySuccess,
	onCopy,
	onSelectChild,
	coverage,
	coverageLoading,
	currentPage,
	totalPages,
	onPage,
	hasActiveFilter,
	unreadByAgentId,
}: IPuListProps) => {
	if (!rows.length) {
		return (
			<div className='qa-empty-filter'>
				<p>{hasActiveFilter ? 'No results match your filters.' : 'No results to show.'}</p>
			</div>
		);
	}

	return (
		<>
			<ul className='qa-list'>
				{rows.map((item) => (
					<PuRow
						key={item.id}
						item={item}
						isCopied={copySuccess && copiedCode === item.code}
						onCopy={onCopy}
						onSelectChild={onSelectChild}
						coverage={coverage.get(item.code)}
						coverageLoading={coverageLoading}
						unread={(() => {
							const id = coverage.get(item.code)?.agentId;
							return id ? (unreadByAgentId?.get(id) ?? 0) : 0;
						})()}
					/>
				))}
			</ul>

			{totalPages > 1 && (
				<div className='qa-pager'>
					<button
						type='button'
						className='qa-pager__btn'
						disabled={currentPage <= 1}
						onClick={() => onPage(currentPage - 1)}
					>
						Prev
					</button>
					<span className='qa-pager__status'>
						Page {currentPage} of {totalPages}
					</span>
					<button
						type='button'
						className='qa-pager__btn'
						disabled={currentPage >= totalPages}
						onClick={() => onPage(currentPage + 1)}
					>
						Next
					</button>
				</div>
			)}
			{totalPages > 1 && <p className='qa-pager__note'>Search & sort apply to the current page.</p>}
		</>
	);
};

// ────────────────────────────────────────────────────────────
// State views
// ────────────────────────────────────────────────────────────
export const EmptyState = ({ entityLabel }: { entityLabel: string }) => (
	<div className='qa-state'>
		<div className='qa-state__icon'>
			<WarnIcon />
		</div>
		<p className='qa-state__title'>No {entityLabel.toLowerCase()} to track yet</p>
		<p className='qa-state__hint'>Results analytics will appear here once your collation area is set up.</p>
	</div>
);

export const ErrorState = ({ onRetry }: { onRetry: () => void }) => (
	<div className='qa-state qa-state--error'>
		<div className='qa-state__icon'>
			<WarnIcon />
		</div>
		<p className='qa-state__title'>Couldn’t load result analytics</p>
		<button
			type='button'
			className='qa-state__retry'
			onClick={onRetry}
		>
			Try again
		</button>
	</div>
);

// Re-export so the container can build labels without importing utils twice.
export { childEntityLabel };
